import { useMutation, useQuery } from '@tanstack/react-query';
import { ExtraQueryParams } from '../helpers/utils/reactQuery';
import ApiService from './ApiService';
import { Mutations, Queries } from 'library/queries';
import {
  getAssembleiasAssessorBffEndpoint,
  getAssembleiasAssessorEndpoint,
} from 'library/Apis';
import { getMergedParamsFromObject } from 'helpers/utils/RequestUtils';

type VoteInfoType = {
  title: string;
  type: string;
  startDate: string;
  city: string;
  address: string;
  totals: {
    convoked: number;
    present: number;
    ablePresent: number;
    guests: number;
    totalPresent: number;
  };
  agendas: {
    agendaId: number;
    title: string;
    present: number;
    ablePresent: number;
    details: DetailsOption[];
    items: VoteOption[];
  }[];
};

export interface VoteOption {
  itemId: number;
  description: string;
  votes: number;
  votesTotal: number;
  abstention: boolean;
}

export interface DetailsOption {
  branch: string;
  nucleus: string;
  present: number;
  ablePresent: number;
  item: VoteOption[];
}

export interface AgendaProps {
  ablePresent: number;
  agendaId: number;
  details: DetailsOption[];
  editDate?: string | null;
  editUser?: string | null;
  items: VoteOption[];
  title: string;
  present: number;
}

export const enum TypeOfCollection {
  FACE = 'FACIAL',
  PHYSICAL = 'FISICA',
  BIOMETRIC = 'BIOMETRIA',
}

export type PresencePages = {
  totalElements: number;
  totalPages: number;
  elements: PresenceElements[];
};

export type PresenceElements = {
  oidConvocado: number;
  nomeConvocado: string;
  cpfCnpj: string;
  agencia: string;
  nucleo: string;
  conta: string;
  coordenador: string;
  colaborador: string;
  cpfCnpjConfirmadaPor: string;
  presencaConfirmadaPor: string;
  tipoPresenca: string;
  dataHoraPresenca: string;
  ldapPresencaAgencia: null | string | number;
  lengthTime: null | string | number;
  tipoAssinatura: TypeOfCollection;
  latitude: string;
  longitude: string;
};

export const useVoteInfos = (
  id: string,
  extra?: ExtraQueryParams<VoteInfoType>
) => {
  const url = `${getAssembleiasAssessorBffEndpoint()}event/${id}/resumed`;

  const queryFn = async () => {
    const req = await ApiService.get<VoteInfoType>(url);
    return req.data;
  };

  return useQuery({
    queryKey: [Queries.GetVoteEditInfos, id],
    staleTime: 1000 * 60 * 10, // 10 minutes
    retry: 2,
    retryDelay: 1000 * 2, // 2 seconds
    ...(extra ?? {}),
    queryFn,
  });
};

export const useGetEvidence = (
  idConvocado: number,
  idEvento: number,
  extra?: ExtraQueryParams<any>
) => {
  const url = `${getAssembleiasAssessorEndpoint()}presenca/metadados?${getMergedParamsFromObject(
    {
      idConvocado: String(idConvocado),
      idEvento: String(idEvento),
    }
  )}`;

  const queryFn = async () => {
    const req = await ApiService.get<any>(url, {
      responseType: 'blob',
    });
    return req.data;
  };

  return useQuery({
    queryKey: [Queries.GetMetadados, idEvento, idConvocado],
    staleTime: 1000 * 60 * 10, // 10 minutes
    retry: 2,
    retryDelay: 1000 * 2, // 2 seconds
    gcTime: Infinity,
    queryFn,
    ...(extra ?? {}),
  });
};

export const useSaveNewVote = () => {
  const url = `${getAssembleiasAssessorBffEndpoint()}event/voting/change`;

  const mutationFn = (data: {
    agendaId: number;
    branch: string;
    nucleus: string;
    ldap: string;
    flagNoVotes: boolean;
    items: {
      itemId: number;
      flagAcclamation: boolean;
      quantity: number;
    }[];
    eventId: number;
  }) => {
    return ApiService.post<any>(url, data);
  };

  return useMutation({
    mutationKey: [Mutations.PostEditVotes],
    mutationFn,
    retry: 1,
    retryDelay: 1000 * 2, // 2 seconds
    gcTime: Infinity,
  });
};

export const useGetBranchAndNucleu = (
  idEvento: string = '',
  extra?: ExtraQueryParams<any>
) => {
  const url = `${getAssembleiasAssessorEndpoint()}presenca/agencia-nucleo?idEvento=${idEvento}`;

  const queryFn = async () => {
    const req = await ApiService.get<any>(url);
    return req.data;
  };

  return useQuery({
    queryKey: [Queries.GetBranchAndNucleou],
    staleTime: 1000 * 60 * 10, // 10 minutes
    retry: 2,
    retryDelay: 1000 * 2, // 2 seconds
    queryFn,
    ...(extra ?? {}),
  });
};

export const useGetGuest = (
  idEvento: string = '',
  page: number,
  size: number,
  texto: string,
  extra?: ExtraQueryParams<any>
) => {
  const url = `${getAssembleiasAssessorEndpoint()}presenca/convidado/?${getMergedParamsFromObject(
    {
      idEvento,
      page,
      size,
    }
  )}`;

  const queryFn = async () => {
    const req = await ApiService.get<any>(url);
    return req.data;
  };

  return useQuery({
    queryKey: [Queries.GetGuest, page, size, texto],
    staleTime: 1000 * 60 * 10, // 10 minutes
    retry: 2,
    retryDelay: 1000 * 2, // 2 seconds
    queryFn,
    ...(extra ?? {}),
  });
};

export const usePostPresencePages = (page: number, size: number) => {
  const url = `${getAssembleiasAssessorEndpoint()}presenca/page`;

  const mutationFn = (data: {
    agencias: string[];
    cpfOrNome: string;
    idEvento: number;
    nucleos: string[];
    page: number;
    size: number;
  }) => {
    return ApiService.post<PresencePages>(`${url}`, {
      agencias: data.agencias,
      cpfOrNome: data.cpfOrNome,
      idEvento: data.idEvento,
      nucleos: data.nucleos,
      page: data.page ?? page,
      size: data.size ?? size,
    });
  };

  return useMutation({
    mutationKey: [Mutations.PostPresencePages, page, size],
    mutationFn,
    retry: 1,
    retryDelay: 1000 * 2, // 2 seconds
    gcTime: Infinity,
  });
};
